import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@mlm/ui';
import {
  createTrainingVideo,
  createTrainingVideoCategory,
  deleteTrainingVideo,
  deleteTrainingVideoCategory,
  listTrainingVideoCategories,
  listTrainingVideos,
  trainingVideoThumbnailUrl,
  updateTrainingVideo,
  updateTrainingVideoCategory,
  uploadTrainingVideoThumbnail,
  type TrainingVideo,
  type TrainingVideoCategory,
} from '../lib/api';

const UNCATEGORIZED = '__none__';

/**
 * Training Videos CMS. Two stacked sections: an admin-managed category list
 * (used as filter chips on the member Training tab) and the video table.
 * Videos store an external embed URL (YouTube/Vimeo/link) — no file uploads;
 * only an optional thumbnail image is stored in R2.
 */
export function TrainingVideosPage() {
  const categoriesQuery = useQuery({
    queryKey: ['admin-training-video-categories'],
    queryFn: listTrainingVideoCategories,
  });
  const [filterCategory, setFilterCategory] = useState<string>('');
  const videosQuery = useQuery({
    queryKey: ['admin-training-videos', filterCategory],
    queryFn: () => listTrainingVideos(filterCategory || undefined),
  });

  const categories = categoriesQuery.data ?? [];
  const categoryName = (id: string | null) =>
    id ? (categories.find((c) => c.id === id)?.name ?? 'Unknown') : 'Uncategorized';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Training Videos</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage the videos members watch on their Training tab. Paste a YouTube, Vimeo, or direct
          video URL — members get an embedded player. Tag each video with a category so they can
          filter (e.g. System Tutorial, Complan Tutorial).
        </p>
      </div>

      <CategorySection categories={categories} loading={categoriesQuery.isLoading} />

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Videos</CardTitle>
          <div className="flex items-center gap-2">
            <Select
              value={filterCategory || 'all'}
              onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="h-9 w-48">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <VideoCreateButton categories={categories} />
          </div>
        </CardHeader>
        <CardContent>
          {videosQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (videosQuery.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No videos yet.</p>
          ) : (
            <ul className="divide-y">
              {videosQuery.data!.map((v) => (
                <VideoRow
                  key={v.id}
                  video={v}
                  categoryName={categoryName(v.categoryId)}
                  categories={categories}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Categories ──

function CategorySection({
  categories,
  loading,
}: {
  categories: TrainingVideoCategory[];
  loading: boolean;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-training-video-categories'] });

  const create = useMutation({
    mutationFn: createTrainingVideoCategory,
    meta: { successMessage: 'Category created.' },
    onSuccess: () => {
      invalidate();
      setName('');
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<TrainingVideoCategory> }) =>
      updateTrainingVideoCategory(id, patch),
    meta: { successMessage: 'Category updated.' },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: deleteTrainingVideoCategory,
    meta: { successMessage: 'Category deleted.' },
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['admin-training-videos'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Categories</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate({ name: name.trim() });
          }}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="New category name (e.g. System Tutorial)"
            className="max-w-xs"
          />
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            + Add
          </Button>
        </form>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : categories.length === 0 ? (
          <p className="text-sm text-muted-foreground">No categories yet.</p>
        ) : (
          <ul className="divide-y">
            {categories.map((c) => (
              <CategoryRow
                key={c.id}
                category={c}
                onSave={(patch) => update.mutate({ id: c.id, patch })}
                onToggleActive={() => update.mutate({ id: c.id, patch: { active: !c.active } })}
                onDelete={() => {
                  if (confirm(`Delete category "${c.name}"? Videos in it become uncategorized.`)) {
                    remove.mutate(c.id);
                  }
                }}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function CategoryRow({
  category,
  onSave,
  onToggleActive,
  onDelete,
}: {
  category: TrainingVideoCategory;
  onSave: (patch: Partial<TrainingVideoCategory>) => void;
  onToggleActive: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(category.name);

  if (editing) {
    return (
      <li className="py-2">
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim() && value.trim() !== category.name) onSave({ name: value.trim() });
            setEditing(false);
          }}
        >
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="h-8 w-48"
            autoFocus
          />
          <Button type="submit" size="sm">
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setValue(category.name);
              setEditing(false);
            }}
          >
            Cancel
          </Button>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-2 py-2">
      <span className="text-sm font-medium">{category.name}</span>
      {!category.active && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Hidden</span>
      )}
      <div className="ml-auto flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Button>
        <Button variant="outline" size="sm" onClick={onToggleActive}>
          {category.active ? 'Hide' : 'Show'}
        </Button>
        <Button variant="outline" size="sm" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </li>
  );
}

// ── Videos ──

function VideoCreateButton({ categories }: { categories: TrainingVideoCategory[] }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)}>+ New video</Button>
      {open && <VideoFormModal categories={categories} onClose={() => setOpen(false)} />}
    </>
  );
}

function VideoRow({
  video,
  categoryName,
  categories,
}: {
  video: TrainingVideo;
  categoryName: string;
  categories: TrainingVideoCategory[];
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const remove = useMutation({
    mutationFn: () => deleteTrainingVideo(video.id),
    meta: { successMessage: 'Video deleted.' },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-training-videos'] }),
  });
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="h-12 w-20 shrink-0 overflow-hidden rounded bg-muted">
        {video.thumbnailKey ? (
          <img
            src={trainingVideoThumbnailUrl(video.id)}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{video.title}</span>
          {!video.active && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              Hidden
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {categoryName} · <span className="truncate">{video.videoUrl}</span>
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
        Edit
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          if (confirm(`Delete video "${video.title}"?`)) remove.mutate();
        }}
      >
        Delete
      </Button>
      {editing && (
        <VideoFormModal video={video} categories={categories} onClose={() => setEditing(false)} />
      )}
    </li>
  );
}

function VideoFormModal({
  video,
  categories,
  onClose,
}: {
  video?: TrainingVideo;
  categories: TrainingVideoCategory[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(video?.title ?? '');
  const [description, setDescription] = useState(video?.description ?? '');
  const [categoryId, setCategoryId] = useState<string>(video?.categoryId ?? UNCATEGORIZED);
  const [videoUrl, setVideoUrl] = useState(video?.videoUrl ?? '');
  const [thumbnailKey, setThumbnailKey] = useState<string | null>(video?.thumbnailKey ?? null);
  const [active, setActive] = useState(video?.active ?? true);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({ mutationFn: uploadTrainingVideoThumbnail });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-training-videos'] });
  const create = useMutation({
    mutationFn: createTrainingVideo,
    meta: { successMessage: 'Training video saved.' },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const update = useMutation({
    mutationFn: (patch: Partial<TrainingVideo>) => updateTrainingVideo(video!.id, patch),
    meta: { successMessage: 'Training video saved.' },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      categoryId: categoryId === UNCATEGORIZED ? null : categoryId,
      videoUrl: videoUrl.trim(),
      thumbnailKey,
      active,
    };
    if (video) update.mutate(payload);
    else create.mutate(payload);
  }

  const pending = create.isPending || update.isPending;
  const err =
    (create.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ??
    (update.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg border bg-card p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold mb-3">{video ? 'Edit video' : 'New video'}</h2>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Video URL (YouTube / Vimeo / direct link)
            </label>
            <Input
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://youtu.be/…"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Category</label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNCATEGORIZED}>Uncategorized</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Description (optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Thumbnail (optional)</label>
            <div className="flex items-center gap-2">
              {thumbnailKey && <span className="text-xs text-green-600">✓ uploaded</span>}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const res = await upload.mutateAsync(file);
                  setThumbnailKey(res.key);
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
              >
                {upload.isPending ? 'Uploading…' : thumbnailKey ? 'Replace' : 'Upload image'}
              </Button>
              {thumbnailKey && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setThumbnailKey(null)}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={active} onCheckedChange={(v) => setActive(v === true)} />
            Visible to members
          </label>
          {err && <p className="text-sm text-destructive">{err}</p>}
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : video ? 'Save' : 'Create'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
